import { Component, input, model } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
} from '@shared';
import { AssistantBuilderFileUploadComponent } from './assistant-builder-file-upload.component';
import {
  AssistantBuilderTraitGenericFilesValue,
  AssistantBuilderTraitGenericFormValues,
  genericConfigurationFileValidator,
} from './assistant-builder-trait-generic-form.validators';

export type AssistantBuilderTraitGenericForm = {
  [K in keyof AssistantBuilderTraitGenericFormValues]: FormControl<
    AssistantBuilderTraitGenericFormValues[K]
  >;
};

@Component({
  selector: 'squadbox-assistant-builder-trait-generic-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    AssistantBuilderFileUploadComponent,
  ],
  template: ` @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <!-- skill.md configuration file (Instructions) — above Knowledge per Figma (§9) -->
      <div class="flex flex-col gap-2">
        <squadbox-assistant-builder-file-upload
          [label]="
            'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.LABEL'
              | translate
          "
          [placeholder]="
            'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.PLACEHOLDER'
              | translate
          "
          filePickerButtonIcon="plus"
          formControlName="configurationFile"
          [fileSupportedExtensions]="configurationFileSupportedExtensions"
          [transactionId]="fileUploadTransactionId()"
        ></squadbox-assistant-builder-file-upload>
        <ul class="typo-caption text-neutral-700 mt-0 space-y-1 list-disc pl-5">
          <li
            [innerHTML]="
              'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.HINT'
                | translate: genericConfigurationFileTranslateParams
            "
          ></li>
        </ul>
      </div>

      <div class="py-2 shrink-0" aria-hidden="true">
        <div class="border-t border-neutral-200"></div>
      </div>

      <!-- Sources (Knowledge) -->
      <squadbox-assistant-builder-file-upload
        [label]="'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.LABEL' | translate"
        [placeholder]="
          'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.PLACEHOLDER' | translate
        "
        filePickerButtonIcon="plus"
        formControlName="sourceFiles"
        [fileSupportedExtensions]="documentUploadSupportedExtensions"
        [transactionId]="fileUploadTransactionId()"
      ></squadbox-assistant-builder-file-upload>
    </form>`,
})
export class AssistantBuilderTraitGenericFormComponent {
  public readonly genericConfigurationFileTranslateParams = {
    configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  } as const;

  public readonly fileUploadTransactionId = input.required<string>();

  public readonly configurationFileSupportedExtensions = ['md'];

  public readonly documentUploadSupportedExtensions: string[] = [
    ...ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  public readonly form = model<FormGroup<AssistantBuilderTraitGenericForm>>(
    new FormGroup<AssistantBuilderTraitGenericForm>(
      {
        sourceFiles: new FormControl<AssistantBuilderTraitGenericFilesValue>(
          {
            value: [],
            disabled: false,
          },
          { nonNullable: true },
        ),
        configurationFile:
          new FormControl<AssistantBuilderTraitGenericFilesValue>(
            {
              value: [],
              disabled: false,
            },
            { nonNullable: true },
          ),
      },
      {
        validators: [genericConfigurationFileValidator],
      },
    ),
  );
}
